import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, X, CalendarDays, Clock, Image as ImageIcon, Search, CheckCircle2, Upload } from 'lucide-react';
import { getYearFromTimestamp } from '../Utils/dateHelpers';

// ==================== HIGHLIGHT FORM MODAL ====================

const HighlightFormModal = ({ onClose, onSubmit }) => {
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    eventName:    '',
    timeline:     '',
    profileImage: null,
    previewUrl:   null,
    days:         7,
  });
  const [submitted, setSubmitted] = useState(false);
  const [dragging,  setDragging]  = useState(false);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, profileImage: file, previewUrl: url }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleImageFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.eventName || !form.timeline || !form.days) return;
    setSubmitted(true);
    setTimeout(() => {
      onSubmit({
        eventName:    form.eventName,
        timeline:     form.timeline,
        profileImage: form.previewUrl || null,
        days:         form.days,
      });
      onClose();
    }, 1200);
  };

  const inputStyle = {
    width:        '100%',
    padding:      '10px 14px',
    borderRadius: '8px',
    border:       '1px solid rgb(52,51,51)',
    background:   'rgb(14,14,14)',
    color:        'white',
    fontSize:     '14px',
    outline:      'none',
    boxSizing:    'border-box',
    transition:   'border-color 0.2s',
  };

  const labelStyle = {
    display:       'flex',
    alignItems:    'center',
    gap:           '7px',
    fontSize:      '12px',
    fontWeight:    600,
    color:         'rgba(255,255,255,0.45)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom:  '8px',
  };

  const fieldWrap = { marginBottom: '20px' };

  return (
    <AnimatePresence>
      {/* ── Backdrop ── */}
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position:       'fixed',
          inset:          0,
          background:     'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(4px)',
          zIndex:         1000,
        }}
      />

      {/* ── Panel — perfectly centred via flexbox on the fixed backdrop ── */}
      <motion.div
        key="panel"
        initial={{ opacity: 0, y: 32, scale: 0.96 }}
        animate={{ opacity: 1, y: 0,  scale: 1    }}
        exit={{   opacity: 0, y: 32, scale: 0.96 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}   // prevent backdrop-close when clicking inside
        style={{
          // ✅ No hardcoded marginTop / pixel offsets — centred purely with fixed + transform
          position:     'fixed',
          top:          '50%',
          left:         '50%',
          transform:    'translate(-50%, -50%)',
          zIndex:       1001,

          // ✅ Fluid width: fills narrow screens, caps at 460px on wide ones
          width:        'min(460px, calc(100vw - 32px))',

          // ✅ Scrollable on very short viewports (e.g. landscape mobile)
          maxHeight:    'calc(100vh - 48px)',
          overflowY:    'auto',

          background:   'rgb(10,10,10)',
          border:       '1px solid rgb(52,51,51)',
          borderRadius: '14px',
          boxShadow:    '0 24px 80px rgba(0,0,0,0.7)',
          marginTop:    -250,
        }}
      >
        {/* Header */}
        <div
          style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        '18px 22px 16px',
            borderBottom:   '1px solid rgb(52,51,51)',
            // ✅ Sticky header so close button stays visible while scrolling
            position:       'sticky',
            background:     'rgb(10,10,10)',
            zIndex:         1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: '8px',
                background: 'rgba(255,255,255,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Camera size={17} color="rgba(255,255,255,0.8)" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'white' }}>
                Update Highlight
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
                Set the featured event on your homepage
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.4)', padding: '4px',
              borderRadius: '6px', transition: 'color 0.2s', flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'white')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ padding: '22px' }}>

          {/* Event Name */}
          <div style={fieldWrap}>
            <label style={labelStyle}>
              <Search size={12} />
              Event Name
            </label>
            <input
              type="text"
              placeholder="e.g. Diwali 2025, Holi, World Cup…"
              value={form.eventName}
              onChange={(e) => set('eventName', e.target.value)}
              required
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.3)')}
              onBlur={(e)  => (e.target.style.borderColor = 'rgb(52,51,51)')}
            />
          </div>

          {/* Timeline */}
          <div style={fieldWrap}>
            <label style={labelStyle}>
              <CalendarDays size={12} />
              Timeline / Date
            </label>
            <input
              type="date"
              value={form.timeline}
              onChange={(e) => set('timeline', e.target.value)}
              required
              style={{ ...inputStyle, colorScheme: 'dark' }}
              onFocus={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.3)')}
              onBlur={(e)  => (e.target.style.borderColor = 'rgb(52,51,51)')}
            />
          </div>

          {/* Profile / Banner Image */}
          <div style={fieldWrap}>
            <label style={labelStyle}>
              <ImageIcon size={12} />
              Profile / Banner Image
            </label>

            {form.previewUrl ? (
              <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                <img
                  src={form.previewUrl}
                  alt="preview"
                  style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, profileImage: null, previewUrl: null }))}
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    background: 'rgba(0,0,0,0.65)', border: 'none',
                    borderRadius: '50%', width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'white',
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                style={{
                  border:       `2px dashed ${dragging ? 'rgba(255,255,255,0.4)' : 'rgb(52,51,51)'}`,
                  borderRadius: '8px',
                  background:    dragging ? 'rgba(255,255,255,0.04)' : 'rgb(14,14,14)',
                  padding:      '28px 16px',
                  textAlign:    'center',
                  cursor:       'pointer',
                  transition:   'all 0.2s',
                }}
              >
                <Upload size={20} color="rgba(255,255,255,0.25)" style={{ margin: '0 auto 8px' }} />
                <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                  Drag & drop or{' '}
                  <span style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'underline' }}>browse</span>
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>
                  PNG, JPG, WEBP — max 5 MB
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleImageFile(e.target.files[0])}
            />
          </div>

          {/* Duration */}
          <div style={fieldWrap}>
            <label style={labelStyle}>
              <Clock size={12} />
              Highlight Duration —{' '}
              <span style={{ color: 'white', marginLeft: 4 }}>
                {form.days} {form.days === 1 ? 'day' : 'days'}
              </span>
            </label>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {[1, 3, 7, 14, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => set('days', d)}
                  style={{
                    padding:      '5px 14px',
                    borderRadius: '20px',
                    border:       `1px solid ${form.days === d ? 'rgba(255,255,255,0.55)' : 'rgb(52,51,51)'}`,
                    background:    form.days === d ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color:         form.days === d ? 'white' : 'rgba(255,255,255,0.45)',
                    fontSize:     '12px',
                    cursor:       'pointer',
                    fontWeight:    form.days === d ? 600 : 400,
                    transition:   'all 0.15s',
                  }}
                >
                  {d}d
                </button>
              ))}
            </div>

            <input
              type="range"
              min={1}
              max={90}
              value={form.days}
              onChange={(e) => set('days', Number(e.target.value))}
              style={{ width: '100%', accentColor: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
            />
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '4px',
            }}>
              <span>1 day</span>
              <span>90 days</span>
            </div>
          </div>

          {/* Submit */}
          <motion.button
            type="submit"
            whileTap={{ scale: 0.97 }}
            disabled={submitted}
            style={{
              width:          '100%',
              padding:        '12px',
              borderRadius:   '9px',
              border:         '1px solid rgb(52,51,51)',
              background:      submitted ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.1)',
              color:           submitted ? 'rgba(255,255,255,0.4)' : 'white',
              fontSize:       '14px',
              fontWeight:     600,
              cursor:          submitted ? 'not-allowed' : 'pointer',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              gap:            '8px',
              transition:     'background 0.2s',
              marginTop:      '4px',
            }}
            onMouseEnter={(e) => { if (!submitted) e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e) => { if (!submitted) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >
            {submitted ? (
              <><CheckCircle2 size={16} /> Applied!</>
            ) : (
              <><Camera size={16} /> Set as Highlight</>
            )}
          </motion.button>

        </form>
      </motion.div>
    </AnimatePresence>
  );
};

// ==================== MAIN COMPONENT ====================

const EventHighlight = ({ event: initialEvent }) => {
  const [event,     setEvent]     = useState(initialEvent);
  const [showForm,  setShowForm]  = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  React.useEffect(() => { setEvent(initialEvent); }, [initialEvent]);

  if (!event) return null;

  const handleHighlightSubmit = ({ eventName, timeline, profileImage, days }) => {
    setEvent((prev) => ({
      ...prev,
      eventname:       eventName    || prev.eventname,
      event_timestamp: timeline     || prev.event_timestamp,
      eventimageurl:   profileImage || prev.eventimageurl,
      highlightDays:   days,
    }));
    setShowBadge(true);
    setTimeout(() => setShowBadge(false), 3000);
    console.log('[EventHighlight] New highlight applied:', { eventName, timeline, profileImage, days });
  };

  return (
    // ✅ Block-level, full width — inherits the 80% container from HomePage
    <div
      className="homepage-main-section"
      style={{
        background: 'none',
        position:   'relative',
        width:      '100%'        // ✅ fills whatever column it lives in
      }}
    >
      {/* ── Event Card ──────────────────────────────────── */}
      <Link
        to={`/event-page?eventname=${event.eventid}&year=${getYearFromTimestamp(event.event_timestamp)}`}
        style={{ textDecoration: 'none', display: 'block' }}
      >
        <motion.div
          className="event-sticker large"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            width:      '75vw',
            border:     '1px solid var(--border-color, rgb(52,51,51))',
            background: 'none',
            overflow:   'hidden',
            borderRadius: '12px',
          }}
        >
          <img
            src={event.eventimageurl}
            alt={event.eventname}
            className="event-image"
            style={{
              // ✅ Fluid image — never wider than its container
              width:      '100%',
              height:     'auto',
              maxHeight:  '200px',      // caps on very wide screens
              objectFit:  'cover',
              display:    'block',
            }}
          />
          <h2 className="event-title" style={{ color: 'white' }}>{event.eventname}</h2>
          <p className="event-date">{event.event_timestamp}</p>

          {/* Highlight duration badge */}
          {event.highlightDays && (
            <div
              style={{
                position:       'absolute',
                top:            10,
                left:           10,
                padding:        '3px 10px',
                borderRadius:   '20px',
                background:     'rgba(0,0,0,0.6)',
                border:         '1px solid rgba(255,255,255,0.15)',
                fontSize:       '11px',
                color:          'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(6px)',
              }}
            >
              ⏱ {event.highlightDays}d highlight
            </div>
          )}
        </motion.div>
      </Link>

      {/* ── Camera Button ──────────────────────────────── */}
      <motion.button
        onClick={(e) => { e.stopPropagation(); setShowForm(true); }}
        whileHover={{ scale: 1.1 }}
        whileTap={{  scale: 0.92 }}
        title="Update Highlight Event"
        style={{
          position:       'absolute',
          bottom:         12,
          right:          12,
          width:          40,
          height:         40,
          borderRadius:   '50%',
          border:         '1px solid rgba(255,255,255,0.2)',
          background:     'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(8px)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          cursor:         'pointer',
          color:          'rgba(255,255,255,0.8)',
          zIndex:         10,
          transition:     'border-color 0.2s, background 0.2s',
          boxShadow:      '0 4px 16px rgba(0,0,0,0.5)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background  = 'rgba(30,30,30,0.85)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)';
          e.currentTarget.style.color       = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background  = 'rgba(0,0,0,0.65)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.color       = 'rgba(255,255,255,0.8)';
        }}
      >
        <Camera size={17} />
      </motion.button>

      {/* ── Success Badge ──────────────────────────────── */}
      <AnimatePresence>
        {showBadge && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{   opacity: 0, y: 8 }}
            style={{
              position:       'absolute',
              bottom:         60,
              right:          12,
              padding:        '6px 14px',
              borderRadius:   '8px',
              background:     'rgba(10,10,10,0.9)',
              border:         '1px solid rgba(255,255,255,0.15)',
              fontSize:       '12px',
              color:          'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(8px)',
              display:        'flex',
              alignItems:     'center',
              gap:            '6px',
              whiteSpace:     'nowrap',
              boxShadow:      '0 4px 20px rgba(0,0,0,0.5)',
              zIndex:         11,
            }}
          >
            <CheckCircle2 size={13} color="#6ee7a0" />
            Highlight updated
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Form Modal ─────────────────────────────────── */}
      {showForm && (
        <HighlightFormModal
          onClose={() => setShowForm(false)}
          onSubmit={handleHighlightSubmit}
        />
      )}
    </div>
  );
};

export default EventHighlight;